import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { downloadCSV } from '../../utils/exportUtils';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext'; 
import { getTenantW2Employees, getTenantTimesheets } from '../../services/crmService'; 
import W2IntakeForm from './W2IntakeForm';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

export default function W2Ledger({ isOperatorView = false }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isTimesheetsLoading, setIsTimesheetsLoading] = useState(false);
  const [showIntake, setShowIntake] = useState(false);
  const [activeTab, setActiveTab] = useState('roster');

  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { tenantId, loading: tenantLoading } = useTenant(); 

  // ==========================================
  // STATE: ROSTER & COMPLIANCE
  // ==========================================
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('ALL');
  const [complianceFilter, setComplianceFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [employeeData, setEmployeeData] = useState([]);

  // ==========================================
  // STATE: OFFBOARDING PROTOCOL
  // ==========================================
  const [offboardTarget, setOffboardTarget] = useState(null);
  const [isProcessingOffboard, setIsProcessingOffboard] = useState(false);

  // ==========================================
  // STATE: TIMESHEETS & SCHEDULING
  // ==========================================
  const [timesheets, setTimesheets] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: '2026-08-02',
    end: '2026-08-15'
  });

  // --- ⚡️ LIVE FIREBASE FETCH (ROSTER) ⚡️ ---
  const fetchEmployees = useCallback(async () => {
    if (tenantLoading || !tenantId) return;
    
    setIsLoading(true);
    try {
      const rawEmployees = typeof getTenantW2Employees === 'function' 
        ? await getTenantW2Employees(tenantId) 
        : []; 
      
      const formattedEmployees = rawEmployees.map(emp => {
        const isCleared = emp.compliance?.i9 && emp.compliance?.w4 && emp.compliance?.wa_notices;
        
        return {
          id: emp.id.substring(0, 8),
          rawId: emp.id,
          name: `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || 'Unknown Employee',
          role: emp.role || 'Retail Associate',
          location: emp.location || 'Unassigned',
          status: emp.status || 'Active',
          isActive: emp.isActive !== false,
          compliance: isCleared ? 'Cleared' : 'Action Required',
          checkHqVaulted: !!emp.checkhq_id, 
          hireDate: emp.hireDate?.toDate ? emp.hireDate.toDate().toLocaleDateString() : 'Pending',
          payType: emp.payType || 'Hourly'
        };
      });

      setEmployeeData(formattedEmployees);
    } catch (error) {
      console.error("Failed to fetch W-2 ledger:", error);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, tenantLoading]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // --- ⚡️ OFFBOARDING PROTOCOL (SOFT DELETE) ⚡️ ---
  const handleExecuteOffboard = async () => {
    if (!offboardTarget || !tenantId) return;
    setIsProcessingOffboard(true);

    try {
      const userRef = doc(db, 'users', offboardTarget.rawId);
      await updateDoc(userRef, {
        status: 'Terminated',
        isActive: false,
        accountType: 'TERMINATED',
        updatedAt: new Date()
      });

      await fetchEmployees();
      setOffboardTarget(null);
    } catch (err) {
      console.error("Critical error during offboarding:", err);
      alert("System Error: Could not execute offboard protocol.");
    } finally {
      setIsProcessingOffboard(false);
    }
  };

  // --- ⚡️ LIVE FIREBASE FETCH (TIMECARDS) ⚡️ ---
  useEffect(() => {
    const fetchTimecards = async () => {
      if (tenantLoading || !tenantId || activeTab !== 'timesheets') return;
      
      setIsTimesheetsLoading(true);
      try {
        const rawTimesheets = typeof getTenantTimesheets === 'function'
          ? await getTenantTimesheets(tenantId, dateRange.start, dateRange.end)
          : [];
        
        setTimesheets(rawTimesheets.map(ts => ({
          id: ts.id,
          rawEmployeeId: ts.employeeId,
          name: ts.employeeName || 'Unknown Employee',
          location: ts.location || 'Unassigned',
          reg: Number(ts.regularHours || 0),
          ot: Number(ts.overtimeHours || 0),
          total: Number(ts.regularHours || 0) + Number(ts.overtimeHours || 0),
          status: ts.status || 'PENDING REVIEW'
        })));
      } catch (error) {
        console.error("Failed to fetch timesheets:", error);
      } finally {
        setIsTimesheetsLoading(false);
      }
    };

    fetchTimecards();
  }, [tenantId, tenantLoading, activeTab, dateRange]);

  // --- Dynamic Filtering Logic (Roster) ---
  const displayData = employeeData.filter(item => {
    const query = searchQuery.toLowerCase();
    const matchesSearch = item.name.toLowerCase().includes(query) || 
                          item.id.toLowerCase().includes(query) || 
                          item.role.toLowerCase().includes(query);
    
    let matchesLocation = locationFilter === 'ALL' || item.location.toUpperCase().includes(locationFilter);
    
    let matchesCompliance = true;
    if (complianceFilter === 'CLEARED') matchesCompliance = item.compliance === 'Cleared';
    if (complianceFilter === 'ACTION REQUIRED') matchesCompliance = item.compliance !== 'Cleared';
    if (complianceFilter === 'VAULT PENDING') matchesCompliance = !item.checkHqVaulted;

    let matchesStatus = true;
    if (statusFilter === 'ACTIVE') matchesStatus = item.status !== 'Terminated';
    if (statusFilter === 'TERMINATED') matchesStatus = item.status === 'Terminated';

    return matchesSearch && matchesLocation && matchesCompliance && matchesStatus;
  });

  const activeCount = employeeData.filter(e => e.status !== 'Terminated').length;
  const missingComplianceCount = employeeData.filter(e => e.status !== 'Terminated' && e.compliance !== 'Cleared').length;

  const handleExport = () => {
    downloadCSV(displayData, 'W2_Workforce_Ledger.csv');
  };

  const toggleApproval = (id) => {
    setTimesheets(prev => prev.map(ts => {
      if (ts.id === id) {
        return { ...ts, status: ts.status === 'APPROVED' ? 'PENDING REVIEW' : 'APPROVED' };
      }
      return ts;
    }));
  };

  return (
    <div className="animate-in fade-in duration-500 pb-12 relative h-full flex flex-col">
      
      {/* Header */}
      <div className="flex justify-between items-end mb-6 border-b border-slate-300 pb-4 shrink-0">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">Workforce & HR Command</h1>
          <p className="text-slate-500 text-sm mt-1 font-medium">Internal retail staff directory, compliance tracking, and CheckHQ payroll sync.</p>
        </div>
        
        {/* Dynamic Header Actions Based on Tab */}
        <div className="flex gap-3">
          {activeTab === 'roster' && (
            <>
              <button 
                onClick={handleExport}
                className="text-[10px] font-bold text-slate-500 hover:text-slate-900 uppercase tracking-widest transition-colors px-4 py-2 border border-slate-300 bg-white"
              >
                <i className="fa-solid fa-download mr-2"></i> Export Roster
              </button>
              <button 
                onClick={() => setShowIntake(true)}
                className="bg-slate-900 hover:bg-slate-800 text-white text-[10px] font-bold py-2 px-6 transition-colors uppercase tracking-widest border border-slate-900 shadow-sm"
              >
                + Dispatch Onboarding Link
              </button>
            </>
          )}
          {activeTab === 'schedule' && (
            <div className="flex items-center gap-2 px-4 py-2 border border-slate-300 bg-white">
              <i className="fa-solid fa-location-dot text-slate-400"></i>
              <select className="text-[10px] font-bold text-slate-700 uppercase tracking-widest bg-transparent focus:outline-none cursor-pointer">
                <option>CORPORATE OFFICE</option>
                <option>MARYSVILLE</option>
                <option>TACOMA</option>
                <option>AUBURN</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex border-b border-slate-300 mb-6 shrink-0">
        <button 
          onClick={() => setActiveTab('roster')} 
          className={`px-6 py-3 text-[10px] font-black uppercase tracking-widest transition-colors border-b-2 ${activeTab === 'roster' ? 'border-emerald-500 text-emerald-600 bg-slate-50' : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}
        >
          Team Ledger & Compliance
        </button>
        <button 
          onClick={() => setActiveTab('schedule')} 
          className={`px-6 py-3 text-[10px] font-black uppercase tracking-widest transition-colors border-b-2 ${activeTab === 'schedule' ? 'border-emerald-500 text-emerald-600 bg-slate-50' : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}
        >
          Schedule Builder
        </button>
        <button 
          onClick={() => setActiveTab('timesheets')} 
          className={`px-6 py-3 text-[10px] font-black uppercase tracking-widest transition-colors border-b-2 ${activeTab === 'timesheets' ? 'border-emerald-500 text-emerald-600 bg-slate-50' : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}
        >
          Timesheet Reconciliation
        </button>
      </div>

      {/* ========================================== */}
      {/* TAB 1: ROSTER & COMPLIANCE                   */}
      {/* ========================================== */}
      {activeTab === 'roster' && (
        <div className="flex-1 flex flex-col min-h-0 animate-in fade-in">
          {/* Aggregate Metrics */}
          <div className="grid grid-cols-4 gap-4 mb-6 shrink-0">
            <div className="bg-white border border-slate-300 shadow-sm p-4 border-l-4 border-l-emerald-500">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Active W-2 Staff</p>
              <p className="text-2xl font-black text-slate-800">{isLoading || tenantLoading ? '...' : activeCount}</p>
            </div>
            <div className="bg-white border border-slate-300 shadow-sm p-4 border-l-4 border-l-amber-500 bg-amber-50/10">
              <p className="text-[10px] font-bold text-amber-700 uppercase tracking-widest mb-1 flex items-center gap-2">
                HR Action Required {missingComplianceCount > 0 && <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse"></span>}
              </p>
              <p className="text-2xl font-black text-amber-700">{isLoading || tenantLoading ? '...' : missingComplianceCount}</p>
            </div>
            <div className="bg-white border border-slate-300 shadow-sm p-4 border-l-4 border-l-blue-500">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">CheckHQ Vault Status</p>
              <p className="text-lg font-black text-slate-800 flex items-center gap-2 mt-1">
                <i className="fa-solid fa-lock text-emerald-500"></i> Synced
              </p>
            </div>
            <div className="bg-white border border-slate-300 shadow-sm p-4 border-l-4 border-l-slate-400">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">WA State Reporting</p>
              <p className="text-xs font-bold text-slate-600 mt-2">DSHS Compliant</p>
            </div>
          </div>

          {/* Control Bar: Search & Filters */}
          <div className="flex gap-4 mb-6 shrink-0">
            <div className="flex-1 relative">
              <i className="fa-solid fa-magnifying-glass absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
              <input 
                type="text" 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="SEARCH BY NAME, ID, OR ROLE..." 
                className="w-full bg-white border border-slate-300 text-slate-800 text-sm font-bold pl-10 pr-4 py-3 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 placeholder:text-slate-400 placeholder:font-bold placeholder:uppercase placeholder:text-[10px] placeholder:tracking-widest"
              />
            </div>
            <select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-white border border-slate-300 text-slate-700 text-[10px] font-bold px-4 py-3 uppercase tracking-widest focus:outline-none focus:border-emerald-500 cursor-pointer w-44 shadow-sm"
            >
              <option value="ACTIVE">STATUS: ACTIVE</option>
              <option value="TERMINATED">STATUS: TERMINATED</option>
              <option value="ALL">STATUS: ALL</option>
            </select>
            <select 
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="bg-white border border-slate-300 text-slate-700 text-[10px] font-bold px-4 py-3 uppercase tracking-widest focus:outline-none focus:border-emerald-500 cursor-pointer w-44 shadow-sm"
            >
              <option value="ALL">LOCATION: ALL</option>
              <option value="MARYSVILLE">MARYSVILLE, WA</option>
              <option value="TACOMA">TACOMA, WA</option>
              <option value="AUBURN">AUBURN, WA</option>
              <option value="HQ">OAK HARBOR (HQ)</option>
            </select>
            <select 
              value={complianceFilter}
              onChange={(e) => setComplianceFilter(e.target.value)}
              className="bg-white border border-slate-300 text-slate-700 text-[10px] font-bold px-4 py-3 uppercase tracking-widest focus:outline-none focus:border-emerald-500 cursor-pointer w-44 shadow-sm"
            >
              <option value="ALL">COMPLIANCE: ALL</option>
              <option value="CLEARED">CLEARED (READY)</option>
              <option value="ACTION REQUIRED">ACTION REQUIRED</option>
              <option value="VAULT PENDING">CHECKHQ PENDING</option>
            </select>
          </div>

          {/* High-Density Ledger Table */}
          <div className="bg-white border border-slate-300 shadow-sm overflow-x-auto relative flex-1 min-h-0 flex flex-col">
            <div className="bg-slate-100 border-b border-slate-300 px-4 py-3 min-w-[1200px] grid grid-cols-12 gap-4 items-center shrink-0">
              <div className="col-span-3 text-[10px] font-bold text-slate-600 uppercase tracking-widest">Employee / ID</div>
              <div className="col-span-3 text-[10px] font-bold text-slate-600 uppercase tracking-widest">Role & Location</div>
              <div className="col-span-3 text-[10px] font-bold text-slate-600 uppercase tracking-widest">HR Compliance (WA State)</div>
              <div className="col-span-2 text-[10px] font-bold text-slate-600 uppercase tracking-widest">CheckHQ Vault</div>
              <div className="col-span-1 text-[10px] font-bold text-slate-600 uppercase tracking-widest text-right">Hire Date</div>
            </div>
            
            <div className="divide-y divide-slate-200 min-w-[1200px] flex-1 overflow-y-auto custom-scrollbar">
              {(isLoading || tenantLoading) && (
                <div className="col-span-12 p-12 flex flex-col items-center justify-center">
                  <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Syncing Roster...</p>
                </div>
              )}

              {!(isLoading || tenantLoading) && displayData.map((emp) => {
                const isTerminated = emp.status === 'Terminated';
                const isCleared = emp.compliance === 'Cleared';
                const initials = emp.name.split(' ').map(n => n[0]).join('').substring(0, 2);

                return (
                  <Link 
                    key={emp.id} 
                    to={`/w2-employee/${emp.id}`} 
                    className={`px-4 py-3 grid grid-cols-12 gap-4 items-center transition-colors group cursor-pointer ${
                      isTerminated ? 'bg-slate-50 opacity-60 hover:opacity-100 grayscale' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="col-span-3 flex items-center gap-3">
                      <div className={`w-9 h-9 flex items-center justify-center font-black text-xs border ${
                        isTerminated ? 'bg-slate-200 text-slate-500 border-slate-300' : 'bg-slate-900 text-white border-slate-700'
                      }`}>
                        {initials}
                      </div>
                      <div>
                        <span className={`text-sm font-bold block transition-colors ${
                          isTerminated ? 'text-slate-600' : 'text-slate-900 group-hover:text-blue-700'
                        }`}>
                          {emp.name}
                        </span>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                          ID: {emp.id} • {emp.status}
                        </span>
                      </div>
                    </div>
                    
                    <div className="col-span-3">
                      <span className="text-xs font-bold text-slate-700 block mb-0.5">{emp.role}</span>
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1 block">
                        <i className="fa-solid fa-location-dot mr-1"></i> {emp.location}
                      </span>
                    </div>
                    
                    <div className="col-span-3">
                      {isTerminated ? (
                        <span className="border text-[9px] font-bold px-2 py-0.5 uppercase tracking-widest mb-1 inline-block border-slate-400 text-slate-500 bg-slate-100">
                          Terminated
                        </span>
                      ) : (
                        <span className={`border text-[9px] font-bold px-2 py-0.5 uppercase tracking-widest mb-1 inline-block ${isCleared ? 'border-emerald-500 text-emerald-600 bg-emerald-50' : 'border-amber-500 text-amber-700 bg-amber-50'}`}>
                          {isCleared ? 'I-9 / W-4 Verified' : 'Paperwork Missing'}
                        </span>
                      )}
                      <span className={`text-[10px] font-medium block mt-0.5 ${
                        isTerminated ? 'text-slate-400' : isCleared ? 'text-slate-500' : 'text-amber-600'
                      }`}>
                        {isTerminated ? 'Offboarded' : isCleared ? 'PFML Notices Acknowledged' : 'Awaiting Signatures'}
                      </span>
                    </div>
                    
                    <div className="col-span-2">
                      {!isTerminated && (emp.checkHqVaulted ? (
                        <div className="flex items-center gap-2 text-emerald-600">
                          <i className="fa-solid fa-lock text-sm"></i>
                          <div>
                            <span className="text-xs font-bold block">Vaulted</span>
                            <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 block">Payroll Sync Active</span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-amber-500">
                          <i className="fa-solid fa-lock-open text-sm animate-pulse"></i>
                          <div>
                            <span className="text-xs font-bold block">Awaiting Data</span>
                            <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 block">Not Synced</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="col-span-1 flex flex-col items-end justify-center">
                       <span className="text-sm font-black text-slate-800 block">{emp.hireDate}</span>
                       <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-2">{emp.payType}</span>

                       {!isTerminated && (
                         <button 
                           onClick={(e) => {
                             e.preventDefault();
                             e.stopPropagation();
                             setOffboardTarget(emp);
                           }}
                           className="text-[9px] font-bold text-red-500 hover:text-white hover:bg-red-500 border border-transparent hover:border-red-600 px-2 py-1 uppercase tracking-widest transition-colors shadow-sm"
                         >
                           Offboard
                         </button>
                       )}
                    </div>
                  </Link>
                )
              })}
              
              {!(isLoading || tenantLoading) && displayData.length === 0 && (
                <div className="col-span-12 p-8 text-center text-slate-500 text-xs font-bold uppercase tracking-widest">
                  No employees match your criteria.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ========================================== */}
      {/* TAB 2: SCHEDULE BUILDER                      */}
      {/* ========================================== */}
      {activeTab === 'schedule' && (
        <div className="flex-1 flex flex-col min-h-0 animate-in fade-in">
          <div className="grid grid-cols-4 gap-4 mb-6 shrink-0">
            <div className="bg-white border border-slate-300 p-4 shadow-sm">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1"><i className="fa-solid fa-clock"></i> TARGET HOURS</p>
              <p className="text-xl font-black text-slate-800">0.0 <span className="text-xs text-slate-400 font-bold">hrs</span></p>
            </div>
            <div className="bg-white border border-slate-300 p-4 shadow-sm">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1"><i className="fa-solid fa-money-bill"></i> ESTIMATED LABOR COST</p>
              <p className="text-xl font-black text-slate-800">$0.00</p>
            </div>
            <div className="bg-white border border-slate-300 p-4 shadow-sm">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1"><i className="fa-solid fa-percent"></i> CURRENT LABOR %</p>
              <p className="text-xl font-black text-slate-800">0.0%</p>
            </div>
            <div className="bg-white border border-slate-300 p-4 shadow-sm border-l-4 border-l-amber-400">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1"><i className="fa-solid fa-triangle-exclamation"></i> SHIFT SHORTAGE</p>
              <p className="text-xl font-black text-amber-600">0.0 <span className="text-xs text-amber-400 font-bold">hrs</span></p>
            </div>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 p-4 flex justify-between items-center mb-6 shrink-0">
            <div>
              <h3 className="text-xs font-black text-emerald-900 uppercase tracking-widest flex items-center gap-2">
                <i className="fa-solid fa-robot text-emerald-600"></i> Apex Predictive Auto-Scheduler
              </h3>
              <p className="text-[10px] font-bold text-emerald-700 mt-1">Awaiting W-2 file compilation. The engine will review historical foot traffic to automatically populate the grid below.</p>
            </div>
            <button className="bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold py-2 px-6 uppercase tracking-widest transition-colors shadow-sm opacity-50 cursor-not-allowed">
              Generate AI Schedule
            </button>
          </div>

          <div className="bg-white border border-slate-300 shadow-sm flex-1 flex flex-col min-h-0">
            <div className="border-b border-slate-200 px-4 py-3 flex justify-between items-center bg-slate-50 shrink-0">
              <div className="flex items-center gap-4">
                <button className="text-slate-400 hover:text-slate-700 text-[10px] font-bold uppercase tracking-widest"><i className="fa-solid fa-arrow-left mr-1"></i> Prev</button>
                <span className="text-xs font-black text-slate-900 uppercase tracking-widest">Week of 8/17/2026</span>
                <button className="text-slate-400 hover:text-slate-700 text-[10px] font-bold uppercase tracking-widest">Next <i className="fa-solid fa-arrow-right ml-1"></i></button>
              </div>
              <div className="flex items-center gap-4">
                <button className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest hover:text-emerald-800"><i className="fa-solid fa-copy mr-1"></i> Duplicate to Next Week</button>
                <button className="bg-slate-900 text-white text-[10px] font-bold py-1.5 px-4 uppercase tracking-widest hover:bg-slate-800 transition-colors">Publish Grid To POS</button>
              </div>
            </div>
            
            <div className="grid grid-cols-8 divide-x divide-slate-200 border-b border-slate-200 bg-white shrink-0">
              <div className="p-3"></div>
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => (
                <div key={day} className="p-3 text-center">
                  <p className="text-[10px] font-black text-slate-800 uppercase tracking-widest">{day}</p>
                  <p className="text-[9px] font-bold text-slate-400">{8 + i}/17</p>
                </div>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-slate-200 custom-scrollbar">
              {employeeData.filter(emp => emp.status !== 'Terminated').map(emp => (
                <div key={emp.id} className="grid grid-cols-8 divide-x divide-slate-200 min-h-[80px]">
                  <div className="p-3 bg-slate-50 flex flex-col justify-center">
                    <p className="text-xs font-bold text-slate-900">{emp.name}</p>
                    <p className="text-[9px] font-bold text-slate-400 uppercase mt-0.5">0.0 Hrs</p>
                  </div>
                  {[1,2,3,4,5,6,7].map(cell => (
                    <div key={cell} className="p-2 hover:bg-slate-50 transition-colors cursor-crosshair group relative">
                       <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                         <i className="fa-solid fa-plus text-slate-300 text-lg"></i>
                       </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ========================================== */}
      {/* TAB 3: TIMESHEET RECONCILIATION              */}
      {/* ========================================== */}
      {activeTab === 'timesheets' && (
        <div className="flex-1 flex flex-col min-h-0 animate-in fade-in">
          <div className="bg-white border border-slate-300 shadow-sm flex-1 flex flex-col min-h-0">
            <div className="border-b border-slate-200 px-6 py-4 bg-slate-50 flex justify-between items-center shrink-0">
              <div className="flex items-center gap-4">
                <i className="fa-solid fa-clock-rotate-left text-slate-400 text-xl"></i>
                <div>
                  <h3 className="text-[11px] font-black text-slate-800 uppercase tracking-widest">Timekeeping Records</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Date Range:</span>
                    <input type="date" value={dateRange.start} onChange={(e) => setDateRange({...dateRange, start: e.target.value})} className="w-24 text-[10px] font-bold text-slate-800 border border-slate-300 px-1.5 py-0.5 text-center focus:outline-none focus:border-emerald-500" />
                    <span className="text-[9px] font-bold text-slate-400">-</span>
                    <input type="date" value={dateRange.end} onChange={(e) => setDateRange({...dateRange, end: e.target.value})} className="w-24 text-[10px] font-bold text-slate-800 border border-slate-300 px-1.5 py-0.5 text-center focus:outline-none focus:border-emerald-500" />
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <select className="text-[10px] font-bold text-slate-600 border border-slate-300 bg-white px-3 py-1.5 uppercase tracking-widest focus:outline-none">
                  <option>ALL LOCATIONS</option>
                </select>
                <div className="relative">
                  <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[10px]"></i>
                  <input type="text" placeholder="SEARCH EMPLOYEES..." className="pl-8 pr-3 py-1.5 text-[10px] font-bold text-slate-800 border border-slate-300 w-48 uppercase tracking-widest focus:outline-none focus:border-emerald-500 placeholder:text-slate-400" />
                </div>
                <button className="bg-slate-900 text-white text-[10px] font-bold py-1.5 px-4 uppercase tracking-widest hover:bg-slate-800 transition-colors flex items-center gap-2">
                  <i className="fa-solid fa-plus"></i> Log Manual Punch
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar relative">
              {isTimesheetsLoading ? (
                 <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Pulling Timecards...</p>
                 </div>
              ) : timesheets.length === 0 ? (
                 <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                    <i className="fa-solid fa-clock text-4xl text-slate-200 mb-4"></i>
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">No time entries recorded for this date range.</p>
                 </div>
              ) : (
                <table className="w-full text-left border-collapse">
                  <thead className="bg-white sticky top-0 z-10 shadow-sm border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-[10px] font-black text-slate-500 uppercase tracking-widest">Employee</th>
                      <th className="px-6 py-3 text-[10px] font-black text-slate-500 uppercase tracking-widest">Location</th>
                      <th className="px-6 py-3 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">Regular Hours</th>
                      <th className="px-6 py-3 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">Overtime Hours</th>
                      <th className="px-6 py-3 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">Total Hours</th>
                      <th className="px-6 py-3 text-[10px] font-black text-slate-500 uppercase tracking-widest text-center">Approval Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {timesheets.map((ts) => (
                      <tr key={ts.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4">
                          <span className="text-xs font-bold text-slate-900">{ts.name}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{ts.location}</span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="text-sm font-bold text-slate-700">{ts.reg.toFixed(2)}</span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className={`text-sm font-bold ${ts.ot > 0 ? 'text-amber-600' : 'text-slate-700'}`}>{ts.ot.toFixed(2)}</span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="text-sm font-black text-slate-900">{ts.total.toFixed(2)}</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button 
                            onClick={() => toggleApproval(ts.id)}
                            className={`border text-[9px] font-bold px-3 py-1 uppercase tracking-widest transition-colors ${
                              ts.status === 'APPROVED' 
                                ? 'border-emerald-500 text-emerald-600 bg-emerald-50 hover:bg-emerald-100' 
                                : 'border-amber-500 text-amber-700 bg-amber-50 hover:bg-amber-100'
                            }`}
                          >
                            {ts.status === 'APPROVED' ? <><i className="fa-solid fa-check mr-1"></i> Approved</> : <><i className="fa-solid fa-triangle-exclamation mr-1"></i> Pending Review</>}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            
            <div className="bg-slate-900 p-3 flex justify-between items-center shrink-0">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{timesheets.length} Timecards Loaded</span>
              <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-widest flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span> Live Data Stream Connected
              </span>
            </div>
          </div>
        </div>
      )}

      {/* OFFBOARD CONFIRMATION MODAL */}
      {offboardTarget && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white border border-slate-300 shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95">
            <div className="bg-red-600 p-6 flex justify-between items-center">
              <div>
                <h2 className="text-lg font-black text-white uppercase tracking-tight">Offboard Employee</h2>
              </div>
              <button onClick={() => setOffboardTarget(null)} className="text-red-200 hover:text-white">
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
            </div>
            <div className="p-6 bg-slate-50 border-b border-slate-200 text-center">
              <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="fa-solid fa-user-slash text-xl"></i>
              </div>
              <h3 className="text-xl font-black text-slate-900">{offboardTarget.name}</h3>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">ID: {offboardTarget.id}</p>
            </div>
            <div className="p-6 bg-white text-center space-y-4">
              <p className="text-sm font-medium text-slate-700 leading-relaxed">
                You are about to permanently revoke this W-2 employee's platform access.
              </p>
              <div className="bg-slate-100 p-4 border border-slate-200 text-left space-y-2">
                <p className="text-xs text-slate-600"><i className="fa-solid fa-check text-emerald-500 w-4"></i> Timecard history remains intact.</p>
                <p className="text-xs text-slate-600"><i className="fa-solid fa-check text-emerald-500 w-4"></i> Retained for CheckHQ processing.</p>
                <p className="text-xs text-slate-600"><i className="fa-solid fa-xmark text-red-500 w-4"></i> PBAC access instantly revoked.</p>
              </div>
            </div>
            <div className="p-6 bg-slate-100 flex gap-3">
              <button 
                onClick={() => setOffboardTarget(null)} 
                className="flex-1 bg-white border border-slate-300 text-slate-700 text-[10px] font-bold py-3 uppercase tracking-widest hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleExecuteOffboard} 
                disabled={isProcessingOffboard}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white text-[10px] font-bold py-3 uppercase tracking-widest transition-colors shadow-sm disabled:opacity-50"
              >
                {isProcessingOffboard ? 'Processing...' : 'Execute Offboard'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showIntake && <W2IntakeForm onClose={() => setShowIntake(false)} />}
    </div>
  );
}